import { useMemo, useRef, useEffect } from 'react';
import {
  useUpdateParticipantTaskEntryMutation,
  useUpdateParticipantTaskMutation,
} from 'store/api/pkd/endpoints/participantTask';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { TaskStatus } from 'utils/enum';
import { getAttribute } from 'utils/participantTask';
import { uniq } from 'lodash';
import useParticipantTask from 'hooks/useParticipantTask';
import { capitalizeFirstLetter, spinner } from 'utils/string';

export const useQuestionnaire = ({ participantTaskId, onSubmit, onError }) => {
  const pagesVisited = useRef([]);
  const saveTaskEntry = useRef([]);
  const currentPage = useRef(0);
  const pageTitle = useRef('');
  const entryData = useRef({});
  const defaultValues = useRef();
  const actionType = useRef('');
  const { isPersonLoading, person, personId, personAccountProfileId } =
    useGetPersonAndAttributes();

  const personAttributes = Object.fromEntries(
    person?.included?.map?.(({ data: { attributes } }) => [
      attributes?.name,
      attributes?.value,
    ]) ?? []
  );

  const {
    attributes,
    form,
    taskEntry = {},
    taskEntryId,
    participantTask,
    participantTaskTitle,
    isTaskDetailsLoading,
    refetchProfileParticipantTask,
  } = useParticipantTask({
    participantTaskId,
    personAccountProfileId,
  });

  if (!defaultValues.current) {
    defaultValues.current = taskEntry;
  }

  const [updateParticipantTask, { isLoading: isSavingSurveyEntry }] =
    useUpdateParticipantTaskMutation();
  const [
    updateParticipantTaskEntry,
    { isLoading: isUpdateParticipantTaskEntryLoading },
  ] = useUpdateParticipantTaskEntryMutation();

  const completed = participantTask?.status === TaskStatus.Completed;
  const inProgress = participantTask?.status === TaskStatus.Inprogress;

  entryData.current = {
    status : participantTask?.status,
    taskEntry,
    taskEntryId,
  };

  const isLoading =
    isPersonLoading ||
    isSavingSurveyEntry ||
    isTaskDetailsLoading ||
    isUpdateParticipantTaskEntryLoading;

  const creatingParticipantTaskEntryRef = useRef(false);
  const savingParticipantTaskEntryRef = useRef(false);

  const handleTaskEntryChange = (data) => {
    saveTaskEntry.current = data;
    defaultValues.current = data;
  };

  const handleSubmit = async ({ data }) => {
    if (!creatingParticipantTaskEntryRef.current) {
      creatingParticipantTaskEntryRef.current = true;
      const entry = JSON.parse(JSON.stringify(saveTaskEntry.current));
      defaultValues.current = entry;
      const payload = {
        entry,
        status : TaskStatus.Completed,
        participantTaskId,
      };
      let hasError = false;
      try {
        if (
          [TaskStatus.Completed, TaskStatus.Inprogress].includes(
            entryData.current?.status
          )
        ) {
          const { error } = await updateParticipantTaskEntry({
            personId,
            entries                : entry,
            status                 : TaskStatus.Completed,
            participantTaskId,
            participantTaskEntryId : entryData.current?.taskEntryId,
          });
          
          hasError |= Boolean(error);
        } else {
          const { error } = await updateParticipantTask(payload);
          hasError |= Boolean(error);
        }

        creatingParticipantTaskEntryRef.current = false;
        hasError ? onError() : onSubmit();
      } catch (error) {
        onError();
      } finally {
        refetchProfileParticipantTask();
        creatingParticipantTaskEntryRef.current = false;
      }
    }
  };

  const handleSave = async ({ page }) => {
    if (!savingParticipantTaskEntryRef.current) {
      actionType.current = 'save';
      const entry = JSON.parse(JSON.stringify(saveTaskEntry.current));
      currentPage.current = page;
      savingParticipantTaskEntryRef.current = true;
      const payload = {
        entry,
        participantTaskId,
      };
      defaultValues.current = entry;
      if (!inProgress) {
        payload['status'] = TaskStatus.Inprogress;
      }

      try {
        if (
          [TaskStatus.Completed, TaskStatus.Inprogress].includes(
            entryData.current?.status
          )
        ) {
          await updateParticipantTaskEntry({
            personId,
            entries                : entry,
            status                 : entryData.current?.status,
            participantTaskId,
            participantTaskEntryId : entryData.current?.taskEntryId,
          });
        } else {
          await updateParticipantTask(payload);
        }
      } catch (error) {
        onError();
      } finally {
        refetchProfileParticipantTask();
        savingParticipantTaskEntryRef.current = false;
      }
    }
  };

  const handleNext = async ({ pageTitle }) => {
    pagesVisited.current = uniq(
      [...pagesVisited.current, pageTitle]?.filter((label) => label)
    );
  };

  const startScreenData = useMemo(
    () => getAttribute(attributes, 'meta'),
    [attributes]
  );

  const celebrationScreenData = useMemo(
    () => getAttribute(attributes, 'celebrationScreen'),
    [attributes]
  );
  const coreQuestionnaireKidneyTransplant =
    defaultValues.current?.haveYouHadAKidneyTransplant;
  const kidneyTransplant =
    !isLoading &&
    (!coreQuestionnaireKidneyTransplant
      ? personAttributes?.kidneyTransplant
      : coreQuestionnaireKidneyTransplant
    )?.toLowerCase() === 'yes';

  useEffect(() => {
    const isSaving = savingParticipantTaskEntryRef.current;
    const actionBtn = document.getElementsByClassName?.(
      `btn-wizard-${actionType.current}`
    )?.[0];
    const actionBtns = document.getElementsByClassName?.('btn-wizard');
    for (let index = 0; index < actionBtns.length; index++) {
      const btn = actionBtns[index];
      isSaving
        ? btn.setAttribute('disabled', true)
        : btn.removeAttribute('disabled');
    }
    if (actionBtn) {
      actionBtn.innerHTML = isSaving
        ? `<div style='display: flex; align-items: center; justify-content: center'>${capitalizeFirstLetter(
          actionType.current
        )}${spinner(actionType.current !== 'save' ? '#fff' : null)}</div>`
        : capitalizeFirstLetter(actionType.current);
    }
  }, [savingParticipantTaskEntryRef.current]);
 
  const defaultEntries = {
    kidneyTransplant,
    ...taskEntry,
    ...(defaultValues.current ?? {}),
  };
  return {
    participantTask,
    participantTaskTitle,
    form,
    defaultValues : defaultEntries,
    readOnly      : !(startScreenData.editable || !completed),
    isLoading,
    attributes,
    startScreenData,
    page          : currentPage.current,
    celebrationScreenData,
    taskStatus    : participantTask?.status,
    completed,
    isSubmitting  : creatingParticipantTaskEntryRef.current,
    handleSubmit,
    handleNext,
    handleSave,
    handleTaskEntryChange,
  };
};
